import { useState } from 'react';
import { Flex, View } from '../..';
import { useTabContext } from './TabContext';
import { getBorderStyles, variantStyles } from './Tab.helpers';
import styled, { useTheme } from 'styled-components';
import { Text } from '../Text/Text';

/**
 * Prop descriptions for Tabs
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultVariantTabIndicator = styled.div.withConfig({
  displayName: "Tab__DefaultVariantTabIndicator",
  componentId: "k1i0gl-0"
})(({
  theme: _theme,
  $visible
}) => {
  const theme = _theme;
  return {
    backgroundColor: theme.color['fill-accent-neutral-pressed'],
    borderRadius: theme.borderRadius['100'],
    height: '5px',
    top: '2.5px',
    marginBlockStart: '-4px',
    position: 'relative',
    transition: 'opacity 0.2s ease-in-out',
    opacity: $visible ? 1 : 0
  };
});
const StyledTabWrapper = styled.div.withConfig({
  displayName: "Tab__StyledTabWrapper",
  componentId: "k1i0gl-1"
})(["", ""], props => props.$flexGrow && `flex-grow: ${props.$flexGrow};`);
const StyledTabText = styled.div.withConfig({
  displayName: "Tab__StyledTabText",
  componentId: "k1i0gl-2"
})(["cursor:", ";"], props => props.$disabled ? 'not-allowed' : 'pointer');
const InternalTitle = ({
  title,
  isActive,
  hover
}) => {
  const isString = typeof title === 'string';
  const as = isString ? 'p' : 'span';
  return /*#__PURE__*/_jsx(Text, {
    as: as,
    typography: isActive ? 'heading-50' : 'body-100',
    color: hover && !isActive ? 'icon-primary-hover' : 'text-primary-default',
    children: title
  });
};
export const Tab = props => {
  return props.children ? /*#__PURE__*/_jsx(View, {
    paddingBlock: "300",
    role: "tabpanel",
    tabIndex: 0,
    "aria-labelledby": `tab-${props.id}`,
    id: `panel-${props.id}`,
    children: props.children
  }) : /*#__PURE__*/_jsx(_Fragment, {});
};
export const InternalTabItem = ({
  active,
  disabled = false,
  flexGrow,
  onKeyDown,
  icon: Icon,
  id,
  onFocus,
  onSelectedChange,
  refs,
  title,
  variant = 'default'
}) => {
  const {
    selected,
    tabIds
  } = useTabContext();
  const isActive = active || id === selected;
  const [hover, setHover] = useState(false);
  const getTabState = () => {
    if (isActive) {
      return disabled ? 'disabled-active' : 'active';
    }
    if (hover) {
      return 'hover';
    }
    if (disabled) {
      return 'disabled';
    }
    return 'idle';
  };
  const styleAttributes = variantStyles[variant][getTabState()];
  const tabIndex = isActive ? 0 : -1;
  const theme = useTheme();
  return /*#__PURE__*/_jsx(StyledTabWrapper, {
    role: "tab",
    ref: refs[tabIds.indexOf(id)],
    onKeyDown: onKeyDown,
    tabIndex: tabIndex,
    "data-tab-id": id,
    onFocus: () => onFocus(id),
    "aria-selected": isActive,
    "aria-controls": `panel-${id}`,
    "aria-disabled": disabled ? true : undefined,
    $flexGrow: flexGrow,
    children: /*#__PURE__*/_jsxs(Flex, {
      flexGrow: flexGrow,
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(View, Object.assign({
        paddingInline: "700"
      }, styleAttributes, getBorderStyles(variant, getTabState(), tabIds.indexOf(id), tabIds.length - 1, tabIds.indexOf(selected)), {
        children: /*#__PURE__*/_jsx(StyledTabText, {
          onMouseEnter: () => setHover(true),
          onMouseLeave: () => setHover(false),
          $disabled: disabled,
          onClick: () => {
            if (!disabled) {
              if (id !== selected && onSelectedChange) {
                onSelectedChange(id);
              }
            }
          },
          children: /*#__PURE__*/_jsxs(Flex, {
            blockSize: theme.space['1100'],
            justifyContent: "center",
            alignItems: "center",
            children: [Icon && /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              paddingInlineEnd: "200",
              children: /*#__PURE__*/_jsx(Icon, {
                size: "icon-400"
              })
            }), /*#__PURE__*/_jsx(InternalTitle, {
              isActive: isActive,
              title: title,
              hover: hover
            })]
          })
        })
      })), variant === 'default' && /*#__PURE__*/_jsx(DefaultVariantTabIndicator, {
        $visible: isActive && variant === 'default',
        "aria-hidden": !isActive
      })]
    })
  });
};